import {
    decodeFilterAndSort,
    encodeFilterAndSort,
    FilterAndSortExpression
} from "../Client/FilterAndSortExpression"
import { Result } from "../Utility/Result"
import { UserPlan } from "./UserModel"
import {
    ConstFilterOperator,
    FilterExpression,
    UnaryFilterOperator
} from "../Client/FilterExpression"
import {
    assertValidDate,
    takeFirst,
    takeLast
} from "../../Helpers/Functions/dateFormat"
import { asError } from "../../Helpers/Functions/error"
import { SortExpression } from "../Client/SortExpression"

export enum BackofficeCustomerSortBy {
    created = "created",
    email = "email"
}

export enum BackofficeCustomerOrderBy {
    asc = "asc",
    desc = "desc"
}

interface BackofficeCustomerSortParams {
    sortBy: BackofficeCustomerSortBy
    orderBy: BackofficeCustomerOrderBy
}

export interface BackofficeCustomerQueryModel {
    email: string
    created: string
    searchEmail: string
}

export type BackofficeCustomerParams = BackofficeCustomerSortParams & {
    searchEmail?: string
    createdStartDate?: string
    createdEndDate?: string
}

const defaultBackofficeSort: SortExpression<BackofficeCustomerQueryModel> = {
    sortBy: "created",
    desc: true
}

export interface Customer {
    username: string
    email: string
    plan: UserPlan
    created: string
    updated: string
    enabled: boolean
    deleted: boolean
    trialDays?: number
}

export function encodeBackofficeParams({
    searchEmail,
    createdEndDate,
    createdStartDate,
    ...params
}: BackofficeCustomerParams): URLSearchParams {
    const searchParams = new URLSearchParams()

    if (searchEmail !== undefined) {
        searchParams.set("searchEmail", searchEmail)
    }
    if (createdStartDate !== undefined) {
        searchParams.set("createdStartDate", createdStartDate)
    }
    if (createdEndDate !== undefined) {
        searchParams.set("createdEndDate", createdEndDate)
    }

    return encodeBackofficeCustomerSortParams(params, searchParams)
}

function encodeBackofficeCustomerSortParams(
    { sortBy, orderBy }: BackofficeCustomerParams,
    baseSearch?: URLSearchParams
) {
    const search = new URLSearchParams(baseSearch)

    search.set("orderBy", orderBy)
    search.set("sortBy", sortBy)

    return search
}

export function encodeBackofficeFilterAndSort(
    filterAndSort?: FilterAndSortExpression<BackofficeCustomerQueryModel>
): URLSearchParams | undefined {
    return encodeFilterAndSort<BackofficeCustomerQueryModel>(
        filterAndSort,
        defaultBackofficeSort
    )
}

export function decodeBackofficeFilterAndSort(search: URLSearchParams) {
    return decodeFilterAndSort(search, defaultBackofficeSort)
}

export function decodeBackofficeFilterAndSortToBackofficeParams({
    filter,
    sort
}: FilterAndSortExpression<BackofficeCustomerQueryModel>): Result<BackofficeCustomerParams> {
    const sortBy =
        sort.sortBy === "created"
            ? BackofficeCustomerSortBy.created
            : sort.sortBy === "email"
            ? BackofficeCustomerSortBy.email
            : undefined
    if (sortBy === undefined) {
        return Result.error("Unsupported sort field")
    }
    function traverse(
        passed: BackofficeCustomerParams,
        f: FilterExpression<BackofficeCustomerQueryModel>
    ): BackofficeCustomerParams {
        switch (f.fieldName) {
            case "searchEmail":
                switch (f.operator) {
                    case ConstFilterOperator.eq:
                        return {
                            ...passed,
                            searchEmail: f.value
                        }
                    default:
                        throw Error("Unsupported operator")
                }
            case "created":
                const startField = "createdStartDate"
                const endField = "createdEndDate"
                const passedStart = passed[startField]
                const passedEnd = passed[endField]
                let updated = passed
                switch (f.operator) {
                    case UnaryFilterOperator.ge: {
                        assertValidDate(f.value)
                        updated = {
                            ...passed,
                            [startField]:
                                passedStart === undefined
                                    ? f.value
                                    : takeLast(f.value, passedStart)
                        }
                        break
                    }
                    case UnaryFilterOperator.le: {
                        assertValidDate(f.value)

                        updated = {
                            ...passed,
                            [endField]:
                                passedEnd === undefined
                                    ? f.value
                                    : takeFirst(f.value, passedEnd)
                        }
                        break
                    }
                    default:
                        throw Error("Unsupported operator")
                }

                return updated
            default:
                throw Error("Unsupported filtering field")
        }
    }
    try {
        return Result.ok(
            filter.reduce(traverse, {
                sortBy,
                orderBy: sort.desc
                    ? BackofficeCustomerOrderBy.desc
                    : BackofficeCustomerOrderBy.asc
            })
        )
    } catch (e) {
        return Result.error(asError(e).message)
    }
}
